import type { AnalysisRequest, ChatRequest } from '@/types'
import { credentialsManager } from './credentials-manager'

const ANALYSIS_MODEL_ID = 'us.anthropic.claude-haiku-4-5-20251001-v1:0'

const ANALYSIS_SYSTEM_PROMPT = `You are a latency benchmarking expert analyzing Amazon Bedrock model performance data. Structure your analysis into these sections:

## Performance Comparison
Compare TTFT (time to first token), throughput (tokens/second), total response time, and token counts across all benchmarked models. Use specific numbers from the data.

## Model Recommendation
Based on the benchmark data and the prompt type used, recommend which model is best suited. Consider the tradeoff between speed, output quality, and cost.

## Cost Considerations
Discuss relative cost implications based on token counts, throughput differences, and model pricing tiers.

## Guardrail Impact Assessment
If guardrail metrics are present, assess the latency overhead of guardrails on input and output processing. If no guardrail data is present, note that guardrails were not tested.

Format your response as markdown with headers, bullet points, and bold text for readability. Be specific and data-driven.`

const PRIORITY_PROMPTS: Record<string, string> = {
  speed: '\n\nIMPORTANT: The user\'s top priority is SPEED. Weight your recommendation heavily toward the model with the lowest TTFT and highest throughput.',
  cost: '\n\nIMPORTANT: The user\'s top priority is COST EFFICIENCY. Weight your recommendation toward the most cost-effective model.',
  quality: '\n\nIMPORTANT: The user\'s top priority is OUTPUT QUALITY. Weight your recommendation toward the highest quality output.',
}

const CHAT_SYSTEM_PROMPT = `You are a latency benchmarking expert. You have access to detailed benchmark data from Amazon Bedrock, Anthropic API, and Grok (xAI) tests. The user will ask you questions about the benchmark results.

You can analyze:
- TTFT (Time to First Token): client-measured vs Bedrock-reported (BR TTFT)
- Network latency: DNS, TCP, TLS timing from network probes, plus E2E overhead
- Streaming throughput and chunk gap patterns
- Cross-vendor comparisons (Bedrock vs Anthropic vs Grok)
- Guardrail latency impact
- Cross-region vs single-region inference profile differences

Be specific, data-driven, and reference actual numbers. Format responses as markdown.`

function getSelectedRegion(): string {
  try {
    return localStorage.getItem('bedrock-diag-region') || 'us-east-1'
  } catch { /* noop */ }
  return 'us-east-1'
}

async function converseWithBedrock(systemPrompt: string, messages: Array<{ role: string; content: string }>): Promise<string> {
  const creds = await credentialsManager.getAwsCredentials()
  if (!creds) throw new Error('No AWS credentials available for analysis. Please provide credentials in Settings.')

  const region = getSelectedRegion()
  const { BedrockRuntimeClient, ConverseCommand } = await import('@aws-sdk/client-bedrock-runtime')

  const client = new BedrockRuntimeClient({
    region,
    credentials: {
      accessKeyId: creds.accessKeyId,
      secretAccessKey: creds.secretAccessKey,
      ...(creds.sessionToken ? { sessionToken: creds.sessionToken } : {}),
    },
  })

  const response = await client.send(new ConverseCommand({
    modelId: ANALYSIS_MODEL_ID,
    system: [{ text: systemPrompt }],
    messages: messages.map(m => ({
      role: m.role as 'user' | 'assistant',
      content: [{ text: m.content }],
    })),
    inferenceConfig: { maxTokens: 4096 },
  }))

  const output = response.output
  if (output && 'message' in output && output.message?.content) {
    return output.message.content
      .map((block: any) => block.text || '')
      .join('')
  }
  return ''
}

export async function localRequestAnalysis(req: AnalysisRequest): Promise<string> {
  let userMessage = `## Benchmark Results\n\n**Prompt used:** "${(req.prompt || '').slice(0, 500)}"\n**Models tested:** ${(req.modelNames || []).join(', ')}\n\n`
  for (const s of req.stats) {
    userMessage += `**${s.profileName}** (${s.profileType}): calls=${s.callCount}, avg total=${s.avgTotalSec?.toFixed(3)}s, avg TTFT=${s.avgTtftSec?.toFixed(3)}s, throughput=${s.avgThroughput?.toFixed(1)} tok/s\n`
    if (s.avgGuardrailTotalMs > 0) userMessage += `  Guardrail: ${s.avgGuardrailTotalMs?.toFixed(0)}ms\n`
  }
  if (req.results) userMessage += `\n### Summary\n${req.results}\n`

  const systemPrompt = ANALYSIS_SYSTEM_PROMPT + (req.priority && PRIORITY_PROMPTS[req.priority] ? PRIORITY_PROMPTS[req.priority] : '')
  return converseWithBedrock(systemPrompt, [{ role: 'user', content: userMessage }])
}

export async function localSendChatMessage(req: ChatRequest): Promise<string> {
  const ctx = req.benchmarkContext
  if (!ctx) throw new Error('benchmarkContext is required for chat')

  let context = `## Benchmark Data\n\n`
  context += `**Run ID:** ${ctx.runId || 'N/A'}\n`
  context += `**Iterations per profile:** ${ctx.iterationsPerProfile || 'N/A'}\n`
  context += `**Models tested:** ${(ctx.modelNames || []).join(', ') || 'N/A'}\n\n`

  context += `### Aggregated Statistics\n\n`
  for (const s of (ctx.stats || [])) {
    context += `**${s.profileName}** (${s.profileType}):\n`
    context += `- Calls: ${s.callCount}, Total: avg=${s.avgTotalSec?.toFixed(3)}s, TTFT: avg=${s.avgTtftSec?.toFixed(3)}s\n`
    context += `- Throughput: avg=${s.avgThroughput?.toFixed(1)} tok/s\n\n`
  }

  const chatMessages = [
    { role: 'user', content: `Here is the benchmark data:\n\n${context}\n\nPlease acknowledge you have the data.` },
    { role: 'assistant', content: 'I have the complete benchmark data loaded. Ask me anything about the results.' },
    ...req.messages.map(m => ({ role: m.role, content: String(m.content).slice(0, 2000) })),
  ]

  return converseWithBedrock(CHAT_SYSTEM_PROMPT, chatMessages)
}
